import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Modal } from "./index";

interface ConfirmDialogProps {
    isOpen: boolean;
    title: string;
    message: string;
    /**
     * What the sentence leaves unsaid, under it: a breakdown, a list, a date.
     * Kept separate from `message` so the message stays one plain sentence —
     * a dialog people read in a hurry needs its first line to be the answer,
     * and the arithmetic to be checkable underneath rather than in prose.
     */
    details?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Styles the confirm button as destructive and focuses Cancel instead. */
    destructive?: boolean;
    isBusy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /** Anything the decision needs besides yes or no — an option to tick. */
    children?: ReactNode;
}

/**
 * Ask before doing something that cannot be undone in one click.
 *
 * For a destructive action the initial focus goes to Cancel, so pressing Enter
 * out of habit backs out rather than confirming.
 */
export default function ConfirmDialog({
    isOpen,
    title,
    message,
    details,
    confirmLabel,
    cancelLabel,
    destructive = false,
    isBusy = false,
    onConfirm,
    onCancel,
    children,
}: ConfirmDialogProps) {
    const { t } = useTranslation();
    const confirmRef = useRef<HTMLButtonElement>(null);
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const target = destructive ? cancelRef : confirmRef;
        target.current?.focus();
    }, [isOpen, destructive]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            /* No cross. The dialog already has a Cancel button, and Escape and
               the backdrop close it too — a third way to say no, floated over
               the title, was overlapping the very question being asked. */
            showCloseButton={false}
            className="max-w-md w-full mx-4 p-6 rounded-2xl bg-surface-raised"
        >
            <div role="alertdialog" aria-label={title} className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                    {destructive && (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500 dark:bg-red-500/10">
                            <AlertTriangle className="h-5 w-5" />
                        </span>
                    )}
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-content-primary">
                            {title}
                        </h3>
                        <p className="mt-1 text-sm text-content-tertiary">
                            {message}
                        </p>
                        {details && <div className="mt-3">{details}</div>}
                    </div>
                </div>

                {children}

                <div className="flex justify-end gap-2">
                    <button
                        ref={cancelRef}
                        type="button"
                        onClick={onCancel}
                        disabled={isBusy}
                        className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5"
                    >
                        {cancelLabel ?? t("common.cancel", "Cancel")}
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={onConfirm}
                        disabled={isBusy}
                        className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                            destructive ? "bg-red-600" : "bg-brand-600"
                        }`}
                    >
                        {confirmLabel ?? t("common.confirm", "Confirm")}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
